import BreadCrump from '../tools/breadcrump';
import * as React from 'react';
import { config } from "../../data/config";
import { useEffect, useState } from "react";
import ImageUploader from '../tools/uploaderImage';
import axios from 'axios';
import { getCookies } from "cookies-next";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import HeaderOfPage from '../tools/headerOfPage';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';

interface MultilingualField {
    ku: string;
    fa: string;
    en: string;
    ar: string;
    tr: string;
    [key: string]: string;
}

export default function SettingPage() {
  const { t } = useTranslation();
  const cookie = getCookies();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [adminMode, setAdminMode] = useState<'marketing' | 'public'>('marketing');
  const [activeLang, setActiveLang] = useState<LanguageCode>('en');
  const languages = [
    { code: 'ku' as LanguageCode, label: 'Kurdish' },
    { code: 'fa' as LanguageCode, label: 'فارسی' },
    { code: 'en' as LanguageCode, label: 'English' },
    { code: 'ar' as LanguageCode, label: 'العربية' },
    { code: 'tr' as LanguageCode, label: 'Türkçe' }
  ];

  // Helper function to safely access multilingual fields
  const getMultilingualValue = (field: MultilingualField | string | undefined, lang: LanguageCode): string => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[lang] || '';
    }
    return '';
  };

  const breadcrump = [
    { name: 'Dashboard', url: '/dashboard' },
    { name: 'Setting', url: '/dashboard/setting' },
    { name: 'General', url: null },
  ];

  // Load settings
  const load = async () => {
    try {
      const res = await axios.get(
        `${config.url}/v1/dashboard/setting`,
        { headers: { Authorization: 'Bearer ' + cookie['token'] } }
      );
      const message = res.data.message || {};
      // Ensure multilingual structure exists
      if (message) {
        if (typeof message.name === 'string') {
          message.name = { ku: message.name, fa: message.name, en: message.name, ar: message.name, tr: message.name };
        } else if (message.name && message.name.nl) {
          message.name.ku = message.name.nl;
          delete message.name.nl;
          if (!message.name.tr) message.name.tr = '';
        }
        if (typeof message.title === 'string' || !message.title) {
          message.title = message.title ? { ku: message.title, fa: message.title, en: message.title, ar: message.title, tr: message.title } : { ku: '', fa: '', en: '', ar: '', tr: '' };
        } else if (message.title && message.title.nl) {
          message.title.ku = message.title.nl;
          delete message.title.nl;
          if (!message.title.tr) message.title.tr = '';
        }
        if (typeof message.altlogo === 'string' || !message.altlogo) {
          message.altlogo = message.altlogo ? { ku: message.altlogo, fa: message.altlogo, en: message.altlogo, ar: message.altlogo, tr: message.altlogo } : { ku: '', fa: '', en: '', ar: '', tr: '' };
        } else if (message.altlogo && message.altlogo.nl) {
          message.altlogo.ku = message.altlogo.nl;
          delete message.altlogo.nl;
          if (!message.altlogo.tr) message.altlogo.tr = '';
        }
        if (typeof message.description === 'string' || !message.description) {
          message.description = message.description ? { ku: message.description, fa: message.description, en: message.description, ar: message.description, tr: message.description } : { ku: '', fa: '', en: '', ar: '', tr: '' };
        } else if (message.description && message.description.nl) {
          message.description.ku = message.description.nl;
          delete message.description.nl;
          if (!message.description.tr) message.description.tr = '';
        }
        if (typeof message.keywords === 'string' || !message.keywords) {
          message.keywords = message.keywords ? { ku: message.keywords, fa: message.keywords, en: message.keywords, ar: message.keywords, tr: message.keywords } : { ku: '', fa: '', en: '', ar: '', tr: '' };
        } else if (message.keywords && message.keywords.nl) {
          message.keywords.ku = message.keywords.nl;
          delete message.keywords.nl;
          if (!message.keywords.tr) message.keywords.tr = '';
        }
      }
      setData(message);
      setAdminMode(message.adminMode || 'marketing');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: t('Error loading settings')
      });
    } finally {
      setLoading(false);
    }
  };

  // Save settings
  const save = async () => {
    if (!data) return;
    
    setSaving(true);
    setErrors({});
    try {
      await axios.put(
        `${config.url}/v1/dashboard/setting/${data.id}`,
        { ...data, adminMode },
        { headers: { Authorization: 'Bearer ' + cookie['token'] } }
      );
      Swal.fire({
        title: t('ذخیره شد'),
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error: any) {
      const err = error.response?.data;
      if (err?.errors) {
        // Handle validation errors
        const fieldErrors: Record<string, string> = {};
        Object.keys(err.errors).forEach(key => {
          fieldErrors[key] = err.errors[key].message;
        });
        setErrors(fieldErrors);
        Swal.fire({
          icon: 'warning',
          title: t('Please correct invalid fields')
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('Error saving')
        });
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const inputStyle = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition";

  if (loading) {
    return (
      <div className="bg-slate-100 p-4 m-4 min-h-[100vh]">
        <div className="text-center py-10 text-gray-500">{t('در حال بارگذاری...')}</div>
      </div>
    );
  }

  return (
    <div className="bg-slate-100">
      <div className="p-4 m-4 min-h-[100vh]">
        <BreadCrump data={breadcrump} />
        <HeaderOfPage name={t('تنظیمات عمومی')} />

        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" dir="rtl">
            {/* Form */}
            <div className="bg-white p-6 rounded-xl shadow">
              {/* Save Button (Top) */}
              <div className="mb-6 flex justify-end">
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {saving ? t('در حال ذخیره...') : t('ذخیره تغییرات')}
                </button>
              </div>

              <form className="space-y-6">
                {/* Language Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    زبان
                  </label>
                  <div className="flex gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLang(lang.code)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          activeLang === lang.code
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Website Name */}
                <div>
                  <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                    {t('نام وب‌سایت')} ({languages.find(l => l.code === activeLang)?.label})
                  </label>
                  <input
                    type="text"
                    value={getMultilingualValue(data.name, activeLang)}
                    onChange={(e) => setData({ 
                      ...data, 
                      name: { 
                        ...data.name, 
                        [activeLang]: e.target.value 
                      } 
                    })}
                    className={`${inputStyle} ${errors.name ? 'border-red-500 bg-red-50' : ''}`}
                    placeholder={t('نام وب‌سایت را وارد کنید')}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                    {t('عنوان')} ({languages.find(l => l.code === activeLang)?.label})
                  </label>
                  <textarea
                    value={getMultilingualValue(data.title, activeLang)}
                    onChange={(e) => setData({ 
                      ...data, 
                      title: { 
                        ...data.title, 
                        [activeLang]: e.target.value 
                      } 
                    })}
                    rows={3}
                    className={`${inputStyle} ${errors.title ? 'border-red-500 bg-red-50' : ''}`}
                    placeholder={t('عنوان سایت را وارد کنید')}
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>

                {/* Mobile List (LTR input, RTL label) */}
                <div>
                  <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                    {t('لیست موبایل دریافت‌کننده درخواست')}
                  </label>
                  <textarea
                    dir="ltr"
                    value={data.sendermobileslist || ''}
                    onChange={(e) => setData({ ...data, sendermobileslist: e.target.value })}
                    rows={3}
                    className={`${inputStyle} font-mono ${errors.sendermobileslist ? 'border-red-500 bg-red-50' : ''}`}
                    placeholder="09121112233,091244455566"
                  />
                  {errors.sendermobileslist && (
                    <p className="mt-1 text-sm text-red-600">{errors.sendermobileslist}</p>
                  )}
                </div>

                {/* Logo Alt */}
                <div>
                  <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                    {t('متن جایگزین لوگو (Alt)')} ({languages.find(l => l.code === activeLang)?.label})
                  </label>
                  <input
                    type="text"
                    value={getMultilingualValue(data.altlogo, activeLang)}
                    onChange={(e) => setData({ 
                      ...data, 
                      altlogo: { 
                        ...data.altlogo, 
                        [activeLang]: e.target.value 
                      } 
                    })}
                    className={`${inputStyle} ${errors.altlogo ? 'border-red-500 bg-red-50' : ''}`}
                    placeholder={t('متن جایگزین لوگو')}
                  />
                  {errors.altlogo && <p className="mt-1 text-sm text-red-600">{errors.altlogo}</p>}
                </div>

                {/* Keywords */}
                <div>
                  <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                    {t('کلمات کلیدی')} ({languages.find(l => l.code === activeLang)?.label})
                  </label>
                  <textarea
                    value={getMultilingualValue(data.keywords, activeLang)}
                    onChange={(e) => setData({ 
                      ...data, 
                      keywords: { 
                        ...data.keywords, 
                        [activeLang]: e.target.value 
                      } 
                    })}
                    rows={6}
                    className={inputStyle}
                    placeholder={t('کلمات کلیدی را با کاما جدا کنید')}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                    {t('توضیحات')} ({languages.find(l => l.code === activeLang)?.label})
                  </label>
                  <textarea
                    value={getMultilingualValue(data.description, activeLang)}
                    onChange={(e) => setData({ 
                      ...data, 
                      description: { 
                        ...data.description, 
                        [activeLang]: e.target.value 
                      } 
                    })}
                    rows={8}
                    className={inputStyle}
                    placeholder={t('توضیحات سایت را وارد کنید')}
                  />
                </div>

                {/* Admin Mode */}
                <div>
                  <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                    نسخه پنل مدیریت
                  </label>
                  <select
                    value={adminMode}
                    onChange={(e) => setAdminMode(e.target.value as 'marketing' | 'public')}
                    className={inputStyle}
                  >
                    <option value="marketing">نسخه کامل (مارکتینگ)</option>
                    <option value="public">نسخه عمومی (بدون ابزار مارکتینگ)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    در نسخه عمومی، ابزارهای مارکت‌پلیس مانند مدیریت فاکتورها در منو نمایش داده نمی‌شوند.
                  </p>
                </div>

                {/* Save Button (Bottom) */}
                <div className="pt-4 flex justify-end">
                  <button
                    onClick={save}
                    disabled={saving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    {saving ? t('در حال ذخیره...') : t('ذخیره تغییرات')}
                  </button>
                </div>
              </form>
            </div>

            {/* Logo Upload */}
            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="text-lg font-bold text-gray-800 mb-4">{t('آپلود لوگو')}</h2>
              <hr className="mb-4" />
              <ImageUploader id={data.id} component={'Post'} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}